// Global Instructions Rule Applied!
//
// Agent tab (owner-approved mockup v4; company form places it after
// Deals & Rounds, the deal form after Documents): the full "what the agent
// knows" page for one subject — a company (subjectTable 'accounts') or a
// deal ('deals'). Stats strip (pilot format) + complete signal history +
// memory groups + run log. Maintenance actions only — nothing here assigns
// work to anyone.
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Table, Tag } from 'antd'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import dayjs from 'dayjs'
import { FormSection } from '../../../core/components/FormSection.tsx'
import { store } from '../../../state_manager/store.ts'
import { getAccessToken } from '../../../core/auth/authService'
import {
  fetchAgentInstances,
  fetchAgentMemories,
  fetchAgentRuns,
  retireAgentMemory,
  setAgentInstanceStatus
} from '../services/internalAgentsService.ts'
import {
  AgentInstance,
  AgentMemory,
  AgentRun
} from '../../../state_manager/data_models/internal_agents_model.ts'
import { AgentSignalsSection } from './AgentSignalsSection.tsx'
import { CompanyHighlightsSection } from '../../period_highlights/components/CompanyHighlightsSection.tsx'
import { AgentMemoryGroups } from './AgentMemoryGroups.tsx'
import { useOpenInteraction } from '../hooks/useOpenInteraction.ts'
import { AgentDraftsSection } from './AgentDraftsSection.tsx'

interface AgentTabProps {
  /** Subject the follower tracks: 'accounts' (default) or 'deals'. */
  subjectTable?: string
  subjectId: string
  /** Which agent type's instance to show. An accounts subject can carry
   *  two (company_follower AND lp_relationship_manager when an account is
   *  both a portfolio company and an LP); the host renders one tab per
   *  type. Unset = the first instance on the subject (legacy callers). */
  agentTypeKey?: string
  /** Hands `load` to the host form so its Refresh button reloads this tab.
   *  Same contract DocumentsTab exposes via onRefreshReady. */
  onRefreshReady?: ((refreshFn: () => Promise<void>) => void) | null
}

const Stat: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div>
    <span className='block text-[10.5px] tracking-wider uppercase text-gray-400'>{label}</span>
    <span className='text-base font-semibold tabular-nums'>{value}</span>
  </div>
)

export const AgentTab: React.FC<AgentTabProps> = ({
  subjectTable = 'accounts',
  subjectId,
  agentTypeKey,
  onRefreshReady = null
}) => {
  const [instance, setInstance] = useState<AgentInstance | null>(null)
  // A memory's source interaction lives on this subject's Dashboard timeline.
  const openInteraction = useOpenInteraction(subjectTable, subjectId)
  const [memories, setMemories] = useState<AgentMemory[]>([])
  const [runs, setRuns] = useState<AgentRun[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [pausing, setPausing] = useState(false)
  const token = () => getAccessToken(store.getState().auth)

  const load = useCallback(async () => {
    setLoading(true)
    setLoadError(false)
    try {
      const instances = await fetchAgentInstances(agentTypeKey ?? null, token())
      const mine =
        instances.find(
          (i) =>
            i.agent_instances__subject_table === subjectTable &&
            i.agent_instances__subject_id === subjectId &&
            (!agentTypeKey || i.agent_type_key === agentTypeKey)
        ) ?? null
      setInstance(mine)
      if (mine) {
        const [memoryRows, runRows] = await Promise.all([
          // superseded included so the history chains resolve client-side
          fetchAgentMemories(mine.agent_instances__id, true, token()),
          fetchAgentRuns(mine.agent_instances__id, 10, token())
        ])
        setMemories(memoryRows)
        setRuns(runRows)
      } else {
        setMemories([])
        setRuns([])
      }
    } catch (error) {
      // A failed fetch is NOT "no follower" — without this flag the tab's
      // empty state masks backend/validation errors as an unprovisioned agent.
      console.error('[internal-agents] agent tab load failed:', error)
      setLoadError(true)
    } finally {
      setLoading(false)
    }
  }, [subjectTable, subjectId, agentTypeKey])

  useEffect(() => {
    load()
  }, [load])

  // The app shell hides tabs with display:none rather than unmounting them, so
  // a mount-time-only fetch goes stale. `onRefreshReady` hands `load` up to the
  // company form so its Refresh button reloads this panel too (same contract as
  // DocumentsTab / FileUploadManager).
  useEffect(() => {
    if (onRefreshReady) onRefreshReady(load)
  }, [onRefreshReady, load])

  const activeMemoryCount = useMemo(
    () => memories.filter((m) => m.agent_memories__status === 'active').length,
    [memories]
  )
  const totalCost = useMemo(
    () => runs.reduce((sum, r) => sum + r.agent_runs__total_cost_usd, 0),
    [runs]
  )

  const handlePauseToggle = async () => {
    if (!instance) return
    const next = instance.agent_instances__status === 'paused' ? 'active' : 'paused'
    setPausing(true)
    try {
      await setAgentInstanceStatus(instance.agent_instances__id, next, token())
      await load()
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : 'Failed to update follower')
    } finally {
      setPausing(false)
    }
  }

  const handleRetire = async (memoryId: string) => {
    try {
      await retireAgentMemory(memoryId, token())
      await load()
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : 'Failed to retire memory')
    }
  }

  if (!loading && loadError) {
    return (
      <div className='text-gray-500 text-sm p-4 flex items-center gap-3'>
        <span>Couldn't load the agent follower.</span>
        <Button size='small' onClick={() => load()}>
          Retry
        </Button>
      </div>
    )
  }

  const subjectNoun =
    subjectTable === 'deals'
      ? 'deal'
      : subjectTable === 'prospects'
        ? 'prospect'
        : agentTypeKey === 'lp_relationship_manager'
          ? 'investor'
          : 'company'

  if (!loading && !instance) {
    return (
      <div className='text-gray-400 text-sm p-4'>
        No agent is following this {subjectNoun} yet.
      </div>
    )
  }

  const paused = instance?.agent_instances__status === 'paused'
  // Set when the subject stops qualifying for following (a company losing the
  // Portfolio Company role; a deal leaving the Origination/Execution
  // pipelines): following continues for the cfg lag (trailing correspondence
  // is still worth reading) and the sweep pauses the follower when the clock
  // runs out.
  const followUntil = instance?.agent_instances__follow_until

  return (
    <div className='flex flex-col gap-3.5 p-1'>
      <div className='flex gap-6 flex-wrap items-center bg-white border border-gray-100 rounded-lg px-4 py-2.5'>
        <Stat
          label='Status'
          value={
            <span className='text-[12.5px]'>
              {paused ? <Tag color='gold'>Paused</Tag> : <Tag color='green'>Active</Tag>}
              {!paused && followUntil && (
                <Tag color='#d46b08'>Unfollows {dayjs(followUntil).format('MM/DD/YYYY')}</Tag>
              )}
            </span>
          }
        />
        <Stat
          label='Following through'
          value={
            <span className='text-[12.5px] font-medium'>
              {instance?.agent_instances__last_processed_interaction_at
                ? dayjs(instance.agent_instances__last_processed_interaction_at).format('MM/DD/YYYY')
                : '—'}
            </span>
          }
        />
        <Stat label='Runs' value={instance?.prog__runs ?? 0} />
        <Stat label='Cost (last 10 runs)' value={`$${totalCost.toFixed(2)}`} />
        <Stat label='Open signals' value={instance?.prog__open_signals ?? 0} />
        <Stat label='Active memories' value={activeMemoryCount || (instance?.prog__active_memories ?? 0)} />
        <div className='ml-auto'>
          <Button size='small' loading={pausing} onClick={handlePauseToggle}>
            {paused ? 'Resume following' : 'Pause following'}
          </Button>
        </div>
      </div>

      <AgentSignalsSection
        contextTable={subjectTable}
        contextId={subjectId}
        collapseKey='agent_tab_signals'
        maxEvidencePerCard={2}
        agentTypeKeys={agentTypeKey ? [agentTypeKey] : undefined}
      />

      {/* The relationship manager's drafts, in full and with Show history
          (owner, 2026-09-04). The Dashboard carries a capped, hidden-when-
          empty copy for acting on now; this is the durable list, and it sits
          beside the signals that produced it. Gated the same way the
          highlights section below is — one agent type writes drafts, and the
          company follower's tab must not show an LP's correspondence. */}
      {subjectTable === 'accounts' && agentTypeKey === 'lp_relationship_manager' && (
        <AgentDraftsSection contextTable='accounts' contextId={subjectId} collapseKey='agent_tab_drafts' />
      )}

      {/* The company follower's highlight atoms per period (LP agent
          programme Phase 5) — what the fund / LP sets are built from. */}
      {subjectTable === 'accounts' && agentTypeKey === 'company_follower' && (
        <CompanyHighlightsSection accountId={subjectId} hideWhenEmpty />
      )}

      <FormSection
        title='What the agent knows'
        titleColor='purple'
        loading={loading}
        className='shadow-sm border border-gray-200'
        headerActions={<span className='text-gray-400 text-[11.5px]'>{activeMemoryCount} active</span>}
      >
        <AgentMemoryGroups
          memories={memories}
          onOpenInteraction={openInteraction}
          onRetire={(memoryId) => handleRetire(memoryId)}
        />
      </FormSection>

      <FormSection
        title='Runs'
        titleColor='green'
        loading={loading}
        className='shadow-sm border border-gray-200'
        headerActions={<span className='text-gray-400 text-[11.5px]'>last 10</span>}
      >
        <Table
          size='small'
          rowKey='agent_runs__id'
          dataSource={runs}
          pagination={false}
          columns={[
            {
              title: 'Date',
              dataIndex: 'agent_runs__date_created',
              render: (v: string) => dayjs(v).format('MM/DD/YYYY')
            },
            { title: 'Trigger', dataIndex: 'agent_runs__trigger' },
            { title: 'Status', dataIndex: 'agent_runs__status',
              render: (v: string) =>
                v === 'done' ? <Tag color='green'>done</Tag>
                : v === 'error' ? <Tag color='red'>error</Tag>
                : <Tag>{v}</Tag>
            },
            {
              title: 'Window',
              render: (_: unknown, run: AgentRun) =>
                run.agent_runs__window_from
                  ? `${dayjs(run.agent_runs__window_from).format('MM/DD')} → ${
                      run.agent_runs__window_to
                        ? dayjs(run.agent_runs__window_to).format('MM/DD')
                        : '…'
                    }`
                  : '—'
            },
            {
              title: 'Cost',
              dataIndex: 'agent_runs__total_cost_usd',
              align: 'right' as const,
              render: (v: number) => `$${v.toFixed(2)}`
            },
            {
              title: 'Summary',
              dataIndex: 'agent_runs__summary',
              ellipsis: true,
              render: (v: string | null) => <span className='text-gray-500'>{v ?? ''}</span>
            }
          ]}
        />
      </FormSection>
    </div>
  )
}
